import { Article } from '@/modules/articles/model/article';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookmark as bookmarkSolid } from '@fortawesome/free-solid-svg-icons';
import { faBookmark as bookmarkOutline } from '@fortawesome/free-regular-svg-icons';
import { opinionsApi } from '@/modules/articles/api';
import { useEffect, useState } from 'react';
import { authService, useSessionId } from '@/core/auth/export';
import { messageService, MessageServiceConstants, useSubscription } from '@/core/messaging/export';
import { analyticsService, AnalyticsEvent, AnalyticsTypes } from '@/shared/analytics/export';
import { OpinionMap } from '@/modules/articles/model/opinions/opinion-map';

export default function BookmarkButton({
  article,
  providedOpinions,
  onToggle,
}: {
  article: Article;
  providedOpinions?: OpinionMap;
  onToggle?: (bookmark: boolean) => void;
}) {
  const [opinions, setOpinions] = useState<OpinionMap>(providedOpinions || {});
  const sessionId = useSessionId();
  const subject = authService.getSubjectData();
  const bookmark = async () => {
    if (isBookmarked()) {
      analyticsService.recordAnalyticsEvent(AnalyticsEvent.ARTICLE_UN_BOOKMARKED, {
        subject,
        object: {
          type: AnalyticsTypes.ARTICLE,
          id: article.uri,
          title: article.headline,
          genres: article.genre.map(g => ({ id: `${g.scheme}:${g.code}`, name: g.name })),
          subjects: article.subject.map(s => ({ id: `${s.scheme}:${s.code}`, name: s.name })),
          authors: article?.byline,
        },
        session: {
          id: sessionId,
        },
      });
      onToggle && onToggle(false);
      await opinionsApi.removeOpinion(article.uri, 'BOOKMARK');
    } else {
      analyticsService.recordAnalyticsEvent(AnalyticsEvent.ARTICLE_BOOKMARKED, {
        subject,
        object: {
          type: AnalyticsTypes.ARTICLE,
          id: article.uri,
          title: article.headline,
          genres: article.genre.map(g => ({ id: `${g.scheme}:${g.code}`, name: g.name })),
          subjects: article.subject.map(s => ({ id: `${s.scheme}:${s.code}`, name: s.name })),
          authors: article?.byline,
        },
        session: {
          id: sessionId,
        },
      });
      onToggle && onToggle(true);
      await opinionsApi.saveOpinion(article.uri, 'BOOKMARK');
    }

    opinionsApi.getOpinionsOfArticles(article.uri).then(value => {
      setOpinions(value);
      messageService.publish(MessageServiceConstants.BOOKMARKS_CHANGED, value);
    });
  };

  useSubscription<OpinionMap>(MessageServiceConstants.BOOKMARKS_CHANGED, message => {
    setOpinions({ ...opinions, ...message });
  });

  const isBookmarked = () => {
    return opinions[article.uri]?.includes('BOOKMARK');
  };

  // Keyed on the uri, not the article object: callers often rebuild the article literal on
  // every render, and keying on the object refetched opinions on each parent re-render.
  // Batched so that a list mounting one button per row sends a single request.
  useEffect(() => {
    if (providedOpinions || !article.uri) return;
    opinionsApi.getOpinionsOfArticleBatched(article.uri).then(setOpinions);
    // eslint-disable-next-line
  }, [article.uri]);

  useEffect(() => {
    if (providedOpinions) setOpinions(providedOpinions);
  }, [providedOpinions]);

  return (
    <button
      onClick={bookmark}
      style={{ fontFamily: 'Inter' }}
      className={`btn btn-sm btn-link link-warning pt-0 pb-0`}
    >
      {isBookmarked() && <FontAwesomeIcon icon={bookmarkSolid} />}
      {!isBookmarked() && <FontAwesomeIcon icon={bookmarkOutline} />}
    </button>
  );
}
